import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DaaConflictModal } from '@/features/alerts/DaaConflictModal';
import { AppsOverlay } from '@/features/apps/AppsOverlay';
import { ContextLoginGate } from '@/features/auth/ContextLoginGate';
import { DataStreamView } from '@/features/data-stream/DataStreamView';
import { PaneResizer } from '@/features/data-stream/components/PaneResizer';
import { useDebugPaneResize } from '@/features/debug/hooks/useDebugResize';
import { isDebugContext } from '@/features/debug/topicGroups';
import { DroneDetailPanel } from '@/features/drones/DroneDetailPanel';
import { FleetList } from '@/features/drones/FleetList';
import { FlightsOverlay } from '@/features/flights/FlightsOverlay';
import { InfraContext } from '@/features/infra/InfraContext';
import { InvOverlay } from '@/features/inv/InvOverlay';
import { MapCanvas } from '@/features/map/MapCanvas';
import { OIContext } from '@/features/oi/OIContext';
import { RtmOverlay } from '@/features/rtm/RtmOverlay';
import { RightPanel } from '@/features/shell/RightPanel';
import { rightPanelShownIn } from '@/features/shell/rightPanelFlag';
import { SurvContext } from '@/features/surveillance/SurvContext';
import { canViewContext } from '@/lib/auth/access';
import { useCurrentRole } from '@/lib/store/auth';
import { useDebugStore } from '@/lib/store/debug';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { Boxes, Menu, PanelRight } from 'lucide-react';
import { useState } from 'react';

/**
 * Body layout: left panel | map | right panel.
 *
 * ONE STRUCTURE, ALWAYS. Every view state (fullscreen, overlay context, Data
 * Stream, login gate) is expressed as CSS on this single tree rather than as a
 * different tree — because React reconciles by position, and any branch that
 * moved `<MapCanvas />` to a different slot unmounted it. That ran
 * `disposeMap()` → `mapInstance.remove()`, destroying the WebGL context and
 * resetting the camera to the tenant default centre; pilots lost their place
 * every time they expanded the map or clicked a tab.
 *
 * So `<MapCanvas />` is mounted exactly once here, for the lifetime of the
 * body. Views that replace the map layer over it (they are all
 * `absolute inset-0` with an opaque background) and the map is hidden with
 * `visibility`, never unmounted and never removed from layout — keeping its
 * box the same size means no ResizeObserver churn and a pixel-identical camera
 * when it comes back.
 *
 * Fullscreen collapses the side panels to `w-0` instead of dropping them, so
 * the map grows into the space and the panels keep their state.
 *
 * DELIBERATELY NOT ANIMATED. A CSS width transition here looks like the free
 * win it isn't: `lib/map/instance.ts` observes the container and calls
 * `map.resize()`, and `Map.resize()` assigns `canvas.width`/`canvas.height`,
 * which reinitialises — i.e. blanks — the WebGL drawing buffer. It does not
 * repaint synchronously (it only fires events; the redraw lands a frame or
 * more later) and `.mapboxgl-map` has no background-color, so each cleared
 * frame shows the page background through the canvas. Animating the width
 * runs that once per frame for the whole transition, which reads as a white
 * flicker. Snapping the width costs one blank frame instead of ~18.
 *
 * Removing that last frame means never resizing the canvas at all: give the
 * map a fixed full-body box, float the panels over it, and declare the
 * obscured edges with `map.setPadding()`. That's the real fix if the single
 * frame ever becomes a problem.
 */
export function BodyGrid() {
  const ctx = useUiStore((s) => s.activeContext);
  const fullscreen = useUiStore((s) => s.fullscreenMap);
  const debugMode = useUiStore((s) => s.debugMode);
  const sidebarWidth = useDebugStore((s) => s.sidebarWidth);
  const resizeSidebar = useDebugPaneResize('sidebar');
  const selectedDroneId = useDronesStore((s) => s.selectedId);
  const role = useCurrentRole();
  // Debug Logs tab engages only on supported tabs (drones/oi/surv), never inv.
  const debugOn = debugMode && isDebugContext(ctx) && ctx !== 'inv';
  const [lOpen, setLOpen] = useState(false);
  const [rOpen, setROpen] = useState(false);

  // Public visitors see PUBLIC_CONTEXTS only; every other context prompts
  // login. Sprint 26 — 86e22kuvm.
  const gated = !canViewContext(ctx, role);
  // Inventory + RTM + Apps + Flights replace the map area with their overlay.
  const isOverlayCtx = ctx === 'inv' || ctx === 'rtm' || ctx === 'apps' || ctx === 'flights';
  // Data Stream takes over the whole body (mockup body.ds-ctx hides l/r panels
  // + map). It has its own map (useDsMap) carrying its own markers, so it
  // covers the global MapCanvas rather than reusing it.
  const isDs = ctx === 'ds';
  // Views that own the entire main area — no side panels at all.
  const fullBody = gated || isDs;

  // The map stays mounted under every one of these; only its visibility moves.
  const mapCovered = fullBody || isOverlayCtx;
  // Whether each panel applies to this context at all. Fullscreen doesn't
  // appear here: it collapses the panels' width but leaves them mounted, so
  // expanding and restoring keeps fleet scroll position and detail-tab state.
  const leftApplies = !fullBody && !isOverlayCtx;
  // ENG-340: the sidebar is for the Drones and OIs contexts only (docked column
  // and phone sheet). Anywhere else it appears only in debug mode, where it
  // carries just the Debug Logs tab.
  const rightApplies = !fullBody && (rightPanelShownIn(ctx) || debugOn);

  return (
    <main
      className={cn(
        'flex-1 flex relative overflow-hidden',
        // `pb-(--nav-h)` reserves space for the fixed bottom NavRail (64 px + the
        // device safe-area inset, see index.css) at
        // every breakpoint. Previously `pb-14 md:pb-0` left 8 px overlap on
        // mobile and a full 64 px overlap on desktop — the chat pane's input
        // form was rendering under the nav and looked clipped. Fullscreen
        // deliberately drops it so the map runs behind the rail.
        !fullscreen && 'pb-(--nav-h)',
        // Fullscreen lets the map run behind the rail, so the floating map
        // controls and Mapbox attribution lift themselves by the rail height.
        fullscreen && '[--map-inset:var(--nav-h)]',
      )}
    >
      {/* Phone L-panel trigger — hidden when an overlay context replaces the map */}
      {leftApplies && !fullscreen && (
        <Sheet open={lOpen} onOpenChange={setLOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="absolute top-3 left-3 z-20 lg:hidden bg-(--color-surface) shadow-md"
              aria-label="Open list panel"
            >
              <Menu size={16} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="lg:hidden p-0">
            <LeftPanel
              ctx={ctx}
              selectedDroneId={selectedDroneId}
              closeSheet={() => setLOpen(false)}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* Docked left panel. Collapses to zero width in fullscreen rather than
          unmounting, so fleet scroll and detail-tab state survive the toggle. */}
      {leftApplies && (
        <aside
          className={cn(
            'hidden lg:flex flex-col shrink-0 bg-(--color-surface) overflow-hidden',
            fullscreen
              ? 'w-0 border-r-0'
              : 'w-[280px] xl:w-[320px] border-r border-(--color-border)',
          )}
          aria-hidden={fullscreen}
          inert={fullscreen}
        >
          <LeftPanel ctx={ctx} selectedDroneId={selectedDroneId} />
        </aside>
      )}

      {/* Centre area. The map lives here permanently; everything else layers
          on top of it. */}
      <div className="flex-1 relative">
        <div
          className={cn('absolute inset-0', mapCovered && 'invisible pointer-events-none')}
          aria-hidden={mapCovered}
        >
          <MapCanvas covered={mapCovered} />
        </div>

        {/* Never mount a gated context's view under the login gate: it would
            still fetch and stream (DataStreamView taps MQTT, its own map shows
            the Mapbox logo + layer button through the gate) — ENG-281. */}
        {!gated && ctx === 'inv' && <InvOverlay />}
        {!gated && ctx === 'rtm' && <RtmOverlay />}
        {!gated && ctx === 'apps' && <AppsOverlay />}
        {!gated && ctx === 'flights' && <FlightsOverlay />}
        {/* DataStreamView lays out in-flow, so give it a full-size box. */}
        {!gated && isDs && (
          <div className="absolute inset-0">
            <DataStreamView />
          </div>
        )}
        {gated && <ContextLoginGate ctx={ctx} />}
      </div>

      {/* Phone R-panel trigger — one sheet; the panel's own tab bar picks the
          Events / Alerts / AI Intelligence view. */}
      {rightApplies && !fullscreen && (
        <div className="md:hidden absolute top-3 right-3 z-20 flex gap-1.5">
          <Sheet open={rOpen} onOpenChange={setROpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="bg-(--color-surface) shadow-md"
                aria-label="Open events, alerts and AI panel"
              >
                <PanelRight size={16} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 md:hidden">
              <RightPanel ctx={ctx} debugOn={debugOn} />
            </SheetContent>
          </Sheet>
        </div>
      )}

      {/* Docked R-panel — DAA Alerts / Airband Radio on Drones and OIs (ENG-340). */}
      {rightApplies && debugOn && !fullscreen && <PaneResizer onMouseDown={resizeSidebar} />}
      {rightApplies && (
        <aside
          style={fullscreen ? { width: 0 } : debugOn ? { width: sidebarWidth } : undefined}
          className={cn(
            'hidden md:flex flex-col shrink-0 bg-(--color-surface) overflow-hidden',
            fullscreen ? 'border-l-0' : 'border-l border-(--color-border)',
            !debugOn && !fullscreen && 'w-[300px] xl:w-[360px]',
          )}
          aria-hidden={fullscreen}
          inert={fullscreen}
        >
          <RightPanel ctx={ctx} debugOn={debugOn} />
        </aside>
      )}

      {!fullBody && <DaaConflictModal />}
    </main>
  );
}

function LeftPanel({
  ctx,
  selectedDroneId,
  closeSheet,
}: {
  ctx: ReturnType<typeof useUiStore.getState>['activeContext'];
  selectedDroneId: string | null;
  closeSheet?: () => void;
}) {
  switch (ctx) {
    case 'drones':
      return selectedDroneId ? (
        <DroneDetailPanel id={selectedDroneId} />
      ) : (
        <FleetList onSelect={closeSheet} />
      );
    case 'oi':
      return <OIContext onSelect={closeSheet} />;
    case 'surv':
      return <SurvContext />;
    case 'infra':
      return <InfraContext />;
    case 'rtm':
      // Mockup: RTM uses the centre overlay only — no left-panel list.
      return (
        <div className="p-3 text-[0.846em] text-(--color-text3) flex items-center gap-2">
          RTM context uses the fullscreen overlay.
        </div>
      );
    case 'inv':
      return (
        <div className="p-3 text-[0.846em] text-(--color-text3) flex items-center gap-2">
          <Boxes size={14} /> Inventory context uses the fullscreen overlay.
        </div>
      );
    case 'apps':
      return (
        <div className="p-3 text-[0.846em] text-(--color-text3) flex items-center gap-2">
          <Boxes size={14} /> Apps context uses the fullscreen overlay.
        </div>
      );
    default:
      return null;
  }
}
